import MuiDivider from '@mui/material/Divider';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';

export interface DividerProps {
  orientation?: 'horizontal' | 'vertical';
  flexItem?: boolean;
  children?: ReactNode;
  textAlign?: 'left' | 'center' | 'right';
  sx?: SxProps<Theme>;
}

// A left-aligned label sits ON the margin of the content it introduces, and the rule carries on
// from it to the edge — so the short leading line MUI reserves for `textAlign="left"` is dropped,
// along with the padding that would otherwise push the label off that margin. Encoded here rather
// than left to each view: it is a design-system decision, and a view fixing it for itself would
// have to reach past this façade into `.MuiDivider-*` to do it.
// NOT covered by a test, and deliberately so: jsdom resolves neither the `::before` pseudo-element
// nor the wrapper padding back to anything distinguishable from their initial values, so an
// assertion here would pass just as happily with these rules deleted. Verified by eye instead.
const NO_LEADING_RULE = {
  '&::before': { display: 'none' },
  '& .MuiDivider-wrapper': { pl: 0 },
} as const;

export function Divider({ orientation, flexItem, children, textAlign, sx }: DividerProps) {
  // Forward optionals only when set (exactOptionalPropertyTypes).
  return (
    <MuiDivider
      {...(orientation !== undefined ? { orientation } : {})}
      {...(flexItem !== undefined ? { flexItem } : {})}
      {...(textAlign !== undefined ? { textAlign } : {})}
      // MUI merges an sx ARRAY left to right and drops the falsy entries, so the design-system rule
      // goes first and whatever the caller passes still wins over it.
      sx={[textAlign === 'left' && NO_LEADING_RULE, sx] as SxProps<Theme>}
    >
      {children}
    </MuiDivider>
  );
}
